import {useCallback, useEffect, useState} from 'react';
import {
  Stack,
  Typography,
  alpha,
  Box,
  Grid,
  IconButton,
  LinearProgress,
  ListItemText,
  Tooltip
} from '@mui/material';
import {useForm, FormProvider} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {CustomFile, UploadProps} from 'src/minimal-components/upload';
import Scrollbar from 'src/components/scrollbar';
import {RHFSingleUpload} from 'src/minimal-components/hook-form';
import {v4 as uuidv4} from 'uuid'; // Import the UUID function
import {
  fileData,
  fileDatas,
  fileFormat,
  fileThumb
} from 'src/minimal-components/file-thumbnail';
import EncryptUtils from 'src/utils/EncryptUtils';
import * as APIService from '@shared/services/service-handlers/PrivateService';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {enqueueSnackbar} from 'notistack';
import {useAppContext} from '@context/AppContext';
import {getLMSResultsData} from '@shared/services/service-handlers/PrivateService';
import {varFade} from '@components/animate';
import Iconify from '@components/iconify';
import {AnimatePresence, m} from 'framer-motion';
import Loader from './editor/loader';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import {BASE_RECORD_ID, LMS_IS_ADMIN} from 'src/routes/BaseRoute';
import {useModulePreviewContext} from '@context/ModulePreviewContextProvider';

const validationSchema = yup.object().shape({
  uploadResourcesfiles: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.string().notRequired(),
        file: yup.mixed().notRequired()
      })
    )
    .max(3, 'only 3 files can be added'),
  bloBurls: yup.array().of(
    yup
      .object()
      .shape({
        blobid: yup.number(),
        id: yup
          .mixed()
          .test(
            'is-string-or-number',
            'ID must be a string or a number',
            value => typeof value === 'string' || typeof value === 'number'
          ),
        blobUrl: yup.string(),
        name: yup.string(),
        type: yup.string(),
        size: yup.number()
      })
      .nullable()
      .notRequired()
  ),
  displayprogress: yup.array().of(
    yup.object().shape({
      display: yup.boolean(),
      id: yup
        .mixed()
        .test(
          'is-string-or-number',
          'ID must be a string or a number',
          value => typeof value === 'string' || typeof value === 'number'
        ),
      progress: yup.number()
    })
  )
});
type UploadFile = {
  id: string;
  file: CustomFile;
  preview?: string; // Optional if preview is only for new files
};
interface ModuleData {
  id: string;
  name: string;
  expanded: boolean;
  description?: string;
  duration?: string;
  type: string;
  sequence: number;
  module_lock_type_id?: number;
  locked_until_date?: string;
  Module_Completed?: boolean;
  IsUnlocked?: boolean;
  is_completed?: boolean;
  locked_linked_items?: {
    id: string;
    item_name: string;
    entity_completion_sub_type_id: number;
    module_link_id: number;
    module_id: number;
    type: 'file' | 'link' | 'content' | 'assignment' | 'onlinetest' | 'module';
  }[];
  files?: {
    id: string;
    name: string;
    ParentName: string;
    blob_file_extension?: string;
    fileexpanded: boolean;
    time?: string;
    score?: string;
    lock_type_id: number;
    locked_until_date?: string;
    type: string;
    module_id: number;
    sequence: number;
    ChildItems_Completed?: boolean;
    IsUnlocked?: boolean;
    is_completed?: boolean;
    additionalFiles?: {id: string; name: string}[];
    locked_linked_items?: {
      id: string;
      item_name: string;
      entity_completion_sub_type_id: number;
      module_link_id: number;
      module_id: number;
      type:
        | 'file'
        | 'link'
        | 'content'
        | 'assignment'
        | 'onlinetest'
        | 'module';
    }[];
  }[];
}
type Bloburls = {
  id?: string | number;
  blobid?: number;
  name?: string;
  type?: string;
  size?: number;
  blobUrl?: string; // Optional if preview is only for new files
};
type Displayprogress = {
  id?: any;
  display?: boolean;
  progress?: number;
};
interface IKanbanTask {
  id: string;
  uploadResourcesfiles?: UploadFile[];
  bloBurls?: Bloburls[];
  displayprogress?: Displayprogress[];
}
interface Propsnew extends Omit<UploadProps, 'file'> {
  multiple?: boolean;
}

interface Props extends Propsnew {
  task?: IKanbanTask;
  Moduledata?: any;
  uiFields?: any;
  insertdata?: any;
  tableNameEntity?: string;
  HeaderName?: string;
}
interface progressBarData {
  id: string;
  progress: number;
  display: boolean;
}
export default function Resources({
  task,
  Moduledata,
  uiFields,
  insertdata,
  tableNameEntity,
  HeaderName,
  ...other
}: Props) {
  const {
    appState: {applicationName}
  } = useAppContext();
  const uniqueId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );
  const lmsisAdmin = EncryptUtils.getDecryptedData(
    location?.search,
    LMS_IS_ADMIN
  );
  const [resourceloader, setResourceLoder] = useState(false);
  const [uploaded, setUploadedFile] = useState<(string | CustomFile)[]>([]);
  const {ModulePreviewData, SetModulePreviewData} = useModulePreviewContext();
  const [uploadProgress, setUploadProgress] = useState<Displayprogress[]>([]);
  const [deleteloader, setDeleteLoader] = useState(false);
  const [isloaded, setIsLoaded] = useState(false);
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      uploadResourcesfiles: task?.uploadResourcesfiles || [], // Default to existing attachments
      bloBurls: task?.bloBurls || []
    }
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: {errors, isSubmitting}
  } = methods;
  const values = watch();
  const findFileInModule = (
    data: any[],
    moduleId: string,
    fileId: string,
    type: string
  ) => {
    const module = data.find(m => m.id === moduleId);
    if (!module) return null;

    const file = module.files?.find(
      (f: {id: string; type: string}) => f.id === fileId && f.type === type
    );
    return file ?? null;
  };
  const processModulesData = (data: ModuleData[]) => {
    return data.map(module => ({
      ...module,
      files:
        module.files?.map(file => ({
          ...file,
          locked_linked_items:
            typeof file.locked_linked_items === 'string'
              ? JSON.parse(file.locked_linked_items) // 🛠️ Parse if string
              : file.locked_linked_items || [], // Keep array or default to empty
          additionalFiles:
            typeof file.additionalFiles === 'string'
              ? JSON.parse(file.additionalFiles) // 🛠️ Parse if string
              : file.additionalFiles || [] // Keep array or default to empty
        })) || []
    }));
  };
  const syncExpandedStates = (
    transformed: ModuleData[],
    existing: ModuleData[]
  ) => {
    return transformed.map(module => {
      const matchingModule = existing.find(e => e.id === module.id);

      if (!matchingModule) return module;

      // Sync the `expanded` flag
      const updatedModule = {
        ...module,
        expanded: matchingModule.expanded || false,
        files:
          module?.files &&
          module?.files.map(file => {
            const matchingFile =
              matchingModule?.files &&
              matchingModule.files.find(f => f.id === file.id);
            return {
              ...file,
              fileexpanded: matchingFile?.fileexpanded || false
            };
          })
      };

      return updatedModule;
    });
  };
  const init = async () => {
    try {
      setResourceLoder(true);
      if (uiFields) {
        const {sql_data} = uiFields['Resourse_files'];
        let operations;
        if (insertdata) {
          operations = 'SEARCH';
        }
        const data: any = {
          sql_data: sql_data,
          data: {
            link_id: insertdata.data[0].inserted_id,
            tableName: tableNameEntity,
            course_overview_id: uniqueId
          },
          operation: operations
        };
        const encryptRequestData = EncryptUtils.encryptedData(data);
        const result = await APIService.getLMSResultsData(
          applicationName,
          'LMS',
          {
            tk: encryptRequestData
          }
        );
        if (result.data && result.data.length > 0) {
          const updatedEntities = result.data.map(
            (resourceFile: {
              id: any;
              name: any;
              type: any;
              blob_url: any;
              size: any;
            }) => ({
              id: resourceFile.id ?? undefined, // Assign entity_id
              blobid: resourceFile.id ?? undefined, // Assign type_id
              name: resourceFile.name ?? '', // Assign sub_type_id
              type: resourceFile.type ?? '',
              blobUrl: resourceFile.blob_url ?? '',
              size: resourceFile.size ?? 0
            })
          );
          setUploadedFile(updatedEntities);
          const uploadfiles = result.data.map(
            (resourceFile: {
              id: string;
              name: string;
              type: string;
              blob_url: string;
              size: number;
            }): UploadFile => ({
              id: resourceFile.id ?? '', // Fallback to empty string if undefined
              file: {
                id: resourceFile.id ?? '', // Ensure it has an ID
                name: resourceFile.name ?? '', // Name of the file
                type: resourceFile.type ?? '', // File type
                size: resourceFile.size ?? 0, // File size in bytes
                lastModified: new Date().getTime(), // Mock last modified
                preview: resourceFile.blob_url ?? '', // Use blob URL as a preview
                path: undefined, // Default path to undefined
                slice: () => new Blob().slice(), // Mock implementation
                arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
                stream: () => new Blob().stream(), // Mock implementation
                webkitRelativePath: '' // Default empty string
              } as CustomFile,
              preview: resourceFile.blob_url ?? '' // Set optional preview
            })
          );
          const displayprogressdata = result.data.map((res: {id: any}) => ({
            id: res.id ?? undefined,
            display: false,
            progress: 100
          }));
          setUploadedFile(uploadfiles);
          setUploadProgress(displayprogressdata);
          // Update the form's entities field
          await setValue('bloBurls', updatedEntities, {shouldValidate: true});
          await setValue('uploadResourcesfiles', uploadfiles);
          await setValue('displayprogress', displayprogressdata);
        }
        setResourceLoder(false);
      }
      setResourceLoder(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setResourceLoder(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      const fileData = findFileInModule(
        ModulePreviewData,
        insertdata.data[0].module_id,
        insertdata.data[0].inserted_id,
        insertdata.data[0].type
      );
      if (fileData) {
        setResourceLoder(true);
        if (fileData.additionalFiles && fileData.additionalFiles.length > 0) {
          const updatedEntities = fileData.additionalFiles.map(
            (resourceFile: {
              id: any;
              name: any;
              type: any;
              blob_url: any;
              size: any;
            }) => ({
              id: resourceFile.id ?? undefined, // Assign entity_id
              blobid: resourceFile.id ?? undefined, // Assign type_id
              name: resourceFile.name ?? '', // Assign sub_type_id
              type: resourceFile.type ?? '',
              blobUrl: resourceFile.blob_url ?? '',
              size: resourceFile.size ?? 0
            })
          );
          setUploadedFile(updatedEntities);
          const uploadfiles = fileData.additionalFiles.map(
            (resourceFile: {
              id: string;
              name: string;
              type: string;
              blob_url: string;
              size: number;
            }): UploadFile => ({
              id: resourceFile.id ?? '', // Fallback to empty string if undefined
              file: {
                id: resourceFile.id ?? '', // Ensure it has an ID
                name: resourceFile.name ?? '', // Name of the file
                type: resourceFile.type ?? '', // File type
                size: resourceFile.size ?? 0, // File size in bytes
                lastModified: new Date().getTime(), // Mock last modified
                preview: resourceFile.blob_url ?? '', // Use blob URL as a preview
                path: undefined, // Default path to undefined
                slice: () => new Blob().slice(), // Mock implementation
                arrayBuffer: () => Promise.resolve(new ArrayBuffer(0)), // Mock implementation
                stream: () => new Blob().stream(), // Mock implementation
                webkitRelativePath: '' // Default empty string
              } as CustomFile,
              preview: resourceFile.blob_url ?? '' // Set optional preview
            })
          );
          const displayprogressdata = fileData.additionalFiles.map(
            (res: {id: any}) => ({
              id: res.id ?? undefined,
              display: false,
              progress: 100
            })
          );
          setUploadedFile(uploadfiles);
          setUploadProgress(displayprogressdata);
          // Update the form's entities field
          await setValue('bloBurls', updatedEntities, {shouldValidate: true});
          await setValue('uploadResourcesfiles', uploadfiles);
          await setValue('displayprogress', displayprogressdata);
        }
        setResourceLoder(false);
      } else {
        // await init();
      }
    };

    fetchData();
  }, [ModulePreviewData]);
  const uploadFileToServer = async (file: string | CustomFile) => {
    try {
      const {id, fileup, type, endName, originalname} = fileDatas(file) || {
        id: '',
        fileup: '',
        type: '',
        endName: '',
        originalname: ''
      };
      const uniqueID = uuidv4();
      const reqPayload = EncryptUtils.encryptedData({
        userId: `${uniqueID}.${endName}`
      });
      const formData = new FormData();
      if (fileup && originalname) {
        formData.append('file', fileup);
        formData.append('tk', reqPayload);
      }
      const response = await APIService.postUploadData(
        'LMS',
        'module',
        formData,
        'uploadFile',
        progressEvent => {
          if (progressEvent.total) {
            const total = progressEvent.total ?? 0;
            const progress =
              total > 0 ? Math.round((progressEvent.loaded / total) * 100) : 0;

            setUploadProgress((prev: Displayprogress[]) => {
              const itemExists = prev.some(item => item.id === id);

              if (itemExists) {
                return prev.map(item =>
                  item.id === id
                    ? {...item, progress: progress, display: true}
                    : item
                );
              } else {
                return [...prev, {id, progress: progress, display: true}];
              }
            });
          }
        }
      );
      await setUploadProgress(prev =>
        prev.map(item => (item.id === id ? {...item, display: false} : item))
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };
  const handleUploadAll = async (
    files: (string | CustomFile)[] | undefined,
    uploadProgressdata: Displayprogress[]
  ) => {
    if (files) {
      try {
        let insertResult: {data: any[]; module_data: ModuleData[]} | undefined =
          undefined;
        for (const file of files) {
          const updatedFiles = watch('bloBurls') || [];
          const {
            id,
            name = '',
            size = 0,
            type = ''
          } = fileDatas(file) || {id: '', name: '', type: '', size: 0};
          const blobs = watch('bloBurls');
          let isFileAlreadyUploaded;
          if (blobs) {
            isFileAlreadyUploaded = blobs?.some(
              progress => progress?.id === id
            );
          } else {
            isFileAlreadyUploaded = false;
          }
          if (!isFileAlreadyUploaded) {
            const blobUrl = await uploadFileToServer(file); // Wait for each file to finish before starting the next

            // Update the file object with the blob URL
            const existingFileIndex = updatedFiles.findIndex(
              (item: any) => item?.id === id
            );
            if (existingFileIndex !== -1) {
              // Update the existing file's blob URL
              const file = updatedFiles[existingFileIndex];
              if (file) {
                // Update the existing file's blob URL
                file.blobUrl = blobUrl;
              }
            } else {
              // Add the new file with its blob URL
              updatedFiles.push({
                id: id,
                name: name,
                type: type,
                size: size * 1024 * 1024,
                blobUrl: blobUrl
              });
              await setValue('bloBurls', updatedFiles, {
                shouldValidate: true
              });
              setDeleteLoader(true);
              const {sql_data} = uiFields['Resourse_files'];
              const cleanedInsertData = updatedFiles.map(item => ({
                ...item,
                link_id: insertdata.data[0].inserted_id,
                tableName: tableNameEntity
              }));
              const insertRequest: any = {
                sql_data: sql_data,
                data: {
                  id: id,
                  name: name,
                  type: type,
                  size: size * 1024 * 1024,
                  blobUrl: blobUrl,
                  link_id: insertdata.data[0].inserted_id,
                  course_overview_id: uniqueId,
                  tableName: tableNameEntity,
                  isAdmin: lmsisAdmin === 'True'
                },
                operation: 'INSERT',
                dataUpload: 'Multiple'
              };
              const insertEncryptRequest =
                EncryptUtils.encryptedData(insertRequest);
              insertResult = await getLMSResultsData(applicationName, 'LMS', {
                tk: insertEncryptRequest
              });

              // const entities = watch('bloBurls'); // Get current entities from the form
              if (updatedFiles && insertResult !== undefined) {
                const updatedEntities = updatedFiles.map(entity => {
                  if (entity?.blobid) {
                    return entity; // Preserve the existing entity with blobid
                  }
                  // Find the corresponding result for the current entity
                  const match =
                    insertResult !== undefined &&
                    insertResult.data.find(
                      (res: {
                        name: string | undefined;
                        type: string | undefined;
                        size: any;
                      }) =>
                        res.name === entity?.name &&
                        res.type === entity?.type &&
                        res.size &&
                        entity?.size
                    );
                  // If a match is found, assign the entity_id from the result
                  if (match) {
                    return {
                      ...entity,
                      blobid: match.entity_id // Update the entity_id
                    };
                  }
                  return entity;
                });

                // setUploadedFile(updatedEntities);
                // Use setValue to update the entities field
                setValue('bloBurls', updatedEntities, {
                  shouldValidate: true
                });
                setValue('displayprogress', uploadProgressdata);

                setDeleteLoader(false);
              }
            }

            // Update the form value with the updated files array
          } else {
            setDeleteLoader(false);
          }
          setDeleteLoader(false);
        }
        if (insertResult !== undefined && insertResult?.module_data) {
          const transformData = await processModulesData(
            insertResult.module_data
          );
          const updatedTransformed = syncExpandedStates(
            transformData,
            ModulePreviewData
          );
          await SetModulePreviewData(updatedTransformed);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setDeleteLoader(false);
      }
    }
  };
  const inits = async (files: any) => {
    if (files) {
      setUploadedFile((prev: any) => [...prev, ...files]);
      const newFiles = files.filter(
        (file: {id: string}) =>
          !uploadProgress.some(progress => progress.id === file.id)
      );
      const initialProgress = newFiles.map((file: {id: any}) => ({
        id: file.id,
        progress: 0,
        display: true
      }));
      await setUploadProgress(prev => {
        const updatedProgress = [...prev, ...initialProgress];
        handleUploadAll(files, updatedProgress);
        return updatedProgress;
      });
    }

    // await setValue('displayprogress', uploadProgress);
  };

  const RemoveFileFromServer = async (file: string | CustomFile) => {
    try {
      const {fileup, type, originalname} = fileDatas(file) || {
        fileup: '',
        type: '',
        originalname: ''
      };
      const reqPayload = EncryptUtils.encryptedData({
        userId: originalname
      });
      const formData = new FormData();
      if (fileup && originalname) {
        formData.append('file', fileup);
        formData.append('tk', reqPayload);
      }
      const response = await APIService.postUploadData(
        'LMS',
        'module',
        formData,
        'removeFile'
      );
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const maxFiles = 3;
      const existingFiles = values.uploadResourcesfiles || [];
      if (acceptedFiles.length > maxFiles - existingFiles.length) {
        const allowedFiles = acceptedFiles.slice(
          0,
          maxFiles - existingFiles.length
        );
        const newFiles = await Promise.all(
          allowedFiles.map(async file => {
            const uniqueID = uuidv4();
            return {
              id: uniqueID,
              file,
              preview: ''
            };
          })
        );
        inits(newFiles);
        setError('uploadResourcesfiles', {
          message: `Only ${maxFiles} files are allowed. Excess files were not uploaded.`
        });
        setValue('uploadResourcesfiles', [...existingFiles, ...newFiles], {
          shouldValidate: true
        });
      } else {
        const newFiles = await Promise.all(
          acceptedFiles.map(async file => {
            const uniqueID = uuidv4();
            return {
              id: uniqueID,
              file,
              preview: ''
            };
          })
        );
        inits(newFiles);
        setValue('uploadResourcesfiles', [...existingFiles, ...newFiles], {
          shouldValidate: true
        });
      }
    },
    [setValue, values.uploadResourcesfiles]
  );

  const handleRemoveFile = useCallback(
    async (inputFile: any, bloburls: any) => {
      setDeleteLoader(true);
      const filtered = values.uploadResourcesfiles?.filter(fileObj => {
        // const {id} = fileData(fileObj);
        return fileObj?.id !== inputFile.id;
      });
      const blobupdate = values.bloBurls?.filter(fileObj => {
        return fileObj?.id !== inputFile.id;
      });
      const displayUpdate = values.displayprogress?.filter(fileObj => {
        return fileObj?.id !== inputFile.id;
      });
      try {
        const {sql_data} = uiFields['Resourse_files'];
        const deleteRequest: any = {
          sql_data: sql_data,
          data: {
            source_id: bloburls.blobid,
            course_overview_id: uniqueId,
            tableName: tableNameEntity,
            isAdmin: lmsisAdmin === 'True' // Only send entity_id for deletion
          },
          operation: 'DELETE',
          loopOperation: true
        };
        const deleteEncryptRequest = EncryptUtils.encryptedData(deleteRequest);
        const deleteResult = await getLMSResultsData(applicationName, 'LMS', {
          tk: deleteEncryptRequest
        });
        const transformData = await processModulesData(deleteResult.data);
        const updatedTransformed = syncExpandedStates(
          transformData,
          ModulePreviewData
        );
        await SetModulePreviewData(updatedTransformed);
        // await RemoveFileFromServer(inputFile);
        await setValue('uploadResourcesfiles', filtered, {
          shouldValidate: true
        });
        await setValue('bloBurls', blobupdate, {shouldValidate: true});
        await setValue('displayprogress', displayUpdate);
        if (displayUpdate) {
          setUploadProgress(displayUpdate);
        } else {
          setUploadProgress([]);
        }
        setDeleteLoader(false);
      } catch (error) {
        console.error('Error fetching data:', error);

        setDeleteLoader(false);
      }
    },
    [setValue, values.uploadResourcesfiles]
  );

  const handleRemoveAllFiles = useCallback(() => {
    setValue('uploadResourcesfiles', [], {shouldValidate: true});
  }, [setValue]);
  const getFileFormatFromType = (type: string) => {
    if (!type) return null; // Handle undefined or null input
    return type.split('/').pop(); // Split by '/' and return the last part
  };
  const onSubmit = async (datavalue: any) => {
    const attachments = datavalue.uploadResourcesfiles.map(
      (fileObj: UploadFile) => ({
        id: fileObj.id,
        name: fileObj.file.name,
        url: fileObj.preview
      })
    );
    try {
      if (uiFields) {
        const {sql_data} = uiFields['Resourse_files'];
        setIsLoaded(true);
        let deleteData;
        const entities = watch('bloBurls'); // Get current entities from the form
        if (uploaded !== undefined) {
          if (entities && uploaded && uploaded?.length > 0) {
            const updatedEntities = entities.map(entity => {
              // Find matches in the result array
              // const match = uploaded?.find(
              //   (res: {
              //     blobid: any;
              //     name: string | undefined;
              //     type: string | undefined;
              //     entity_id: number | undefined;
              //   }) => {
              //     if (!res.blobid || !res.name || !res.type) {
              //       return false;
              //     }
              //     return (
              //       res?.type === entity?.type &&
              //       res?.name === entity?.name &&
              //       res?.entity_id === entity?.blobid
              //     );
              //   }
              // );

              // If a match is found, update entity_id
              // if (match) {
              //   return {
              //     ...entity,
              //     entity_id: match.entity_id
              //   };
              // }

              // Otherwise, return the entity as is
              return entity;
            });
            const resultEntityIds = uploaded?.map((res: any) => res.blobid);

            const entitiesToDelete = uploaded?.filter((res: any) => {
              // Check if the entity ID exists in the current entities list
              return !updatedEntities.some(
                entity => entity?.blobid === res?.blobid
              );
            });

            // Update entities for deletion
            deleteData = entitiesToDelete.map((res: any) => ({
              source_id: res?.blobid,
              deleted: true
            }));

            // Existing records without deleted flag

            // Use setValue to update the entities field
            setValue('bloBurls', updatedEntities, {shouldValidate: true});
          }
        }
        const insertData = entities?.filter(entity => !entity?.blobid); // New records without an ID
        const updateData = entities?.filter(entity => entity?.blobid); // Existing records with modifications
        // const deleteData = entities?.filter(entity => entity.deleted); // Records marked for deletion

        // Perform INSERT operations
        if (insertData && insertData?.length > 0) {
          const cleanedInsertData = insertData.map(item => ({
            ...item,
            link_id: insertdata.data[0].inserted_id,
            tableName: tableNameEntity,
            course_overview_id: uniqueId,
            isAdmin: lmsisAdmin === 'True'
          }));
          const insertRequest: any = {
            sql_data: sql_data,
            data: cleanedInsertData,
            operation: 'INSERT',
            loopOperation: true,
            dataUpload: 'Multiple'
          };
          const insertEncryptRequest =
            EncryptUtils.encryptedData(insertRequest);
          const insertResult = await getLMSResultsData(applicationName, 'LMS', {
            tk: insertEncryptRequest
          });
          const entities = watch('bloBurls'); // Get current entities from the form
          if (entities) {
            const updatedEntities = entities.map(entity => {
              // Find the corresponding result for the current entity
              const match = insertResult.data.find(
                (res: {
                  name: string | undefined;
                  type: string | undefined;
                  size: any;
                }) =>
                  res.name === entity?.name &&
                  res.type === entity?.type &&
                  res.size &&
                  entity?.size
              );
              // If a match is found, assign the entity_id from the result
              if (match) {
                return {
                  ...entity,
                  blobid: match.entity_id // Update the entity_id
                };
              }

              // If no match is found, return the entity as-is
              return entity;
            });
            // setUploadedFile(updatedEntities);
            // Use setValue to update the entities field
            await setValue('bloBurls', updatedEntities, {
              shouldValidate: true
            });
          }
        }
        if (deleteData && deleteData?.length > 0) {
          // eslint-disable-next-line camelcase
          const cleanedDeleteData = deleteData.map(({source_id}) => ({
            source_id,
            tableName: tableNameEntity,
            course_overview_id: uniqueId,
            isAdmin: lmsisAdmin === 'True' // Only send entity_id for deletion
          }));
          const deleteRequest: any = {
            sql_data: sql_data,
            data: cleanedDeleteData,
            operation: 'DELETE',
            loopOperation: true,
            dataUpload: 'Multiple'
          };
          const deleteEncryptRequest =
            EncryptUtils.encryptedData(deleteRequest);
          const deleteResult = await getLMSResultsData(applicationName, 'LMS', {
            tk: deleteEncryptRequest
          });
          if (entities) {
            // setUploadedFile(entities);
          } else {
            // setUploadedFile([]);
          }
        }
        setIsLoaded(false);
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setIsLoaded(false);
    }
  };

  return resourceloader ? (
    <Stack
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      minHeight={'50vh'}>
      <LoadingScreen />
    </Stack>
  ) : (
    <FormProvider {...methods}>
      <Scrollbar
        sx={{
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column'
          },
          '& .simplebar-placeholder': {
            height: '10px !important'
          }
        }}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            overflowY: 'auto',
            padding: 8,
            margin: 8,
            height: 'calc(100vh - 120px)'
          }}>
          <Stack spacing={1} sx={{pt: 3, pb: 5, px: 2.5}}>
            <Loader show={deleteloader}></Loader>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">
                {HeaderName || 'Attachements'}
              </Typography>
              <RHFSingleUpload
                multiple
                name="uploadResourcesfiles"
                onDropAccepted={handleDrop}
              />
              <Grid container spacing={2} sx={{mt: 2}}>
                <AnimatePresence initial={false}>
                  {values.uploadResourcesfiles &&
                    values?.uploadResourcesfiles.map((file: any) => {
                      const {
                        id,
                        name = '',
                        size = 0,
                        path = '',
                        preview = '',
                        type = ''
                      } = fileDatas(file) || {
                        id: '',
                        name: '',
                        type: '',
                        size: 0,
                        path: '',
                        preview: ''
                      };

                      const isNotFormatFile = typeof file === 'string';
                      const format = getFileFormatFromType(type);
                      return (
                        <Grid
                          key={id}
                          component={m.div}
                          item
                          xs={12}
                          md={10}
                          {...varFade().inUp}
                          sx={{
                            m: 0.5,
                            padding: 0.5,
                            borderRadius: 1.25,
                            overflow: 'hidden',
                            position: 'relative',
                            display: '-webkit-box',
                            paddingTop: '8px !important',
                            border: theme =>
                              `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`
                          }}>
                          <Grid
                            alignItems="center"
                            justifyContent="center"
                            display="flex"
                            item
                            xs={12}
                            md={2}>
                            <Tooltip title={name}>
                              <Stack
                                flexShrink={0}
                                component="span"
                                alignItems="center"
                                justifyContent="center"
                                display="flex"
                                sx={{
                                  width: 'fit-content',
                                  height: 'inherit'
                                }}>
                                <Box
                                  component="img"
                                  src={
                                    format === 'dcm'
                                      ? '/assets/icons/files/dcm.png'
                                      : format
                                        ? fileThumb(format)
                                        : ''
                                  }
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    flexShrink: 0
                                  }}
                                />
                              </Stack>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <ListItemText
                              sx={{padding: '1vh'}}
                              primary={
                                <Typography
                                  variant="h4"
                                  noWrap
                                  sx={{
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap'
                                  }}>
                                  {isNotFormatFile ? file : name}
                                </Typography>
                              }
                              secondary={
                                <Box>
                                  {uploadProgress &&
                                    uploadProgress.find(
                                      item => item.id === id && item.display
                                    ) && (
                                      <LinearProgress
                                        variant="determinate"
                                        value={
                                          uploadProgress.find(
                                            progress => progress.id === id
                                          )?.progress || 0
                                        }
                                        sx={{
                                          margin: '8px 0',
                                          borderRadius: '4px'
                                        }}
                                      />
                                    )}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{display: 'block', textAlign: 'right'}}>
                                    {isNotFormatFile
                                      ? ''
                                      : `${size.toFixed(2)} MB`}
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const matchingBlobUrl = values.bloBurls
                                ? values?.bloBurls.find(blob => blob?.id === id)
                                : undefined;

                              if (matchingBlobUrl) {
                                handleRemoveFile(file, matchingBlobUrl);
                              } else {
                                console.error(
                                  `Blob URL not found for file with id: ${id}`
                                );
                                handleRemoveFile(file, file);
                              }
                            }}
                            sx={{
                              p: 0.5,
                              top: 4,
                              right: 4,
                              position: 'absolute',
                              color: 'common.white',
                              bgcolor: theme =>
                                alpha(theme.palette.grey[900], 0),
                              '&:hover': {
                                bgcolor: theme =>
                                  alpha(theme.palette.grey[900], 0.72)
                              }
                            }}>
                            <Iconify icon="mingcute:close-line" width={14} />
                          </IconButton>
                        </Grid>
                      );
                    })}
                </AnimatePresence>
              </Grid>
            </Stack>

            {/* <Stack display="flex" flexDirection="row-reverse">
              <LoadingButton
                type="submit"
                variant="contained"
                color="info"
                disabled={isSubmitting}
                loading={isloaded}
                onClick={() => {
                  if (!errors) {
                    onSubmit;
                  }
                }}>
                Save
              </LoadingButton>
            </Stack> */}
          </Stack>
        </form>
      </Scrollbar>
    </FormProvider>
  );
}
